import { useState, type FormEvent } from 'react'
import { Link } from 'react-router'
import { ApiError } from '../../api/problem'
import { register } from '../../auth/session'
import { Banner, Button, Input } from '../../design/components'
import { AuthLayout } from './AuthLayout'
import styles from './AuthForm.module.css'

interface Registered {
  message: string
  /** Development only — the API returns the link so the flow works without an inbox. */
  confirmationLink?: string
}

export function RegisterPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<string[]>([])
  const [done, setDone] = useState<Registered | null>(null)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setFieldErrors([])
    setBusy(true)

    try {
      setDone(await register(email, password, displayName.trim() === '' ? null : displayName))
    } catch (cause) {
      if (cause instanceof ApiError && cause.kind === 'validation') {
        // ASP.NET Identity reports "password too short", "email already taken" and
        // friends as a ValidationProblem keyed by error code. The messages are
        // already human-readable, so show them rather than inventing our own.
        setFieldErrors(cause.fieldMessages().map((entry) => entry.message))
      } else if (cause instanceof ApiError && cause.kind === 'network') {
        setError('Could not reach the server. Check that the API is running.')
      } else {
        setError(cause instanceof ApiError ? cause.message : 'Something went wrong.')
      }
    } finally {
      setBusy(false)
    }
  }

  if (done !== null) {
    return (
      <AuthLayout
        title="Check your email"
        footer={
          <>
            Confirmed already? <Link to="/login">Sign in</Link>
          </>
        }
      >
        <Banner tone="ember">{done.message}</Banner>

        {done.confirmationLink !== undefined && (
          <p className={styles.note} style={{ marginTop: 'var(--sp-4)' }}>
            Development build — the API returned the confirmation link directly, so no inbox is
            needed:
            <a className={styles.devLink} href={toClientConfirmUrl(done.confirmationLink)}>
              {toClientConfirmUrl(done.confirmationLink)}
            </a>
          </p>
        )}
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Create an account"
      subtitle="You will need to confirm your email address before you can sign in."
      footer={
        <>
          Already have an account? <Link to="/login">Sign in</Link>
        </>
      }
    >
      <form className={styles.form} onSubmit={(e) => void onSubmit(e)} noValidate>
        {error !== null && (
          <Banner tone="error" urgent>
            {error}
          </Banner>
        )}

        {fieldErrors.length > 0 && (
          <Banner tone="error" urgent>
            <ul className={styles.fieldErrors}>
              {fieldErrors.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </Banner>
        )}

        <Input
          label="Email"
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <Input
          label="Display name"
          name="displayName"
          autoComplete="nickname"
          placeholder="Optional"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />

        <Input
          label="Password"
          type="password"
          name="password"
          autoComplete="new-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint="At least 6 characters, with a digit and a lowercase letter."
        />

        <Button type="submit" fullWidth disabled={busy}>
          {busy ? 'Creating…' : 'Create account'}
        </Button>
      </form>
    </AuthLayout>
  )
}

/**
 * Rewrites the API's confirmation link to this client's own route.
 *
 * The API builds the link against its own host, so following it verbatim lands
 * on a raw JSON response instead of a page. Set `ClientApp:BaseUrl` on the API
 * and it emits a link straight here — this rewrite then finds nothing to do and
 * returns the link unchanged.
 */
function toClientConfirmUrl(link: string): string {
  try {
    const url = new URL(link)
    if (url.pathname.endsWith('/auth/confirm-email') && url.origin !== window.location.origin) {
      return `${window.location.origin}/confirm-email${url.search}`
    }
    return link
  } catch {
    return link
  }
}
